import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Loader2, RefreshCw, AlertTriangle, Users, Sun, Moon, ClipboardList,
  History, ChevronRight, Search, Wrench, ClipboardCheck, ArrowLeftRight,
  Hash, ArrowLeft, FileText, Calendar as CalendarIcon,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { todayLocalIso } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useLivePoll } from "@/hooks/useLivePoll";
import PastSwingDrawer from "@/components/PastSwingDrawer";

/**
 * SwingHistory — Swing Archive page. Two tabs:
 *   1. Timeline: every finished swing on this site, grouped by
 *      (date · shift · crew). Click through to the Shift Summary.
 *   2. WO Lookup: type any work order # (partial match) and see every
 *      inspection where that WO was recorded across all shifts / dates.
 *      Click through to the inspection detail.
 *
 * The page is reachable from the Swing page via a top-right button — it is
 * intentionally not in the main sidebar so the sidebar stays focused on
 * "what's happening right now".
 */
export default function SwingHistoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") || "timeline").toLowerCase();
  const setTab = (t) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set("tab", t);
    return next;
  });

  const [inspections, setInspections] = useState(null);
  const [ppfs, setPpfs] = useState([]);
  const [handovers, setHandovers] = useState([]);
  // Real /swings records — used to power the drill-in drawer with actual
  // inspection_ids[] instead of the client-side date/shift/crew grouping.
  // Josh (Jul 12): tapping a finished swing must open a rugged view-only
  // detail that mirrors the active Swing home so supervisors can see
  // "we only got this digger to 90 %". Client-side grouping loses partials
  // (only completed statuses bubble up), so we prefer the server record.
  const [swingsRaw, setSwingsRaw] = useState([]);
  const [openSwing, setOpenSwing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [woQuery, setWoQuery] = useState(params.get("wo") || "");

  const reload = async () => {
    setBusy(true); setErr("");
    try {
      const [a, b, c, s] = await Promise.all([
        api.get("/inspections").catch(() => ({ data: [] })),
        api.get("/ppfs").catch(() => ({ data: [] })),
        api.get("/handovers").catch(() => ({ data: [] })),
        api.get("/swings", { params: { limit: 200 } }).catch(() => ({ data: [] })),
      ]);
      setInspections(unwrap(a.data));
      setPpfs(unwrap(b.data));
      setHandovers(unwrap(c.data));
      setSwingsRaw(unwrap(s.data));
    } catch (ex) {
      setErr(apiError(ex));
      setInspections([]);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { reload(); }, []);
  useLivePoll(reload, 5000, true);

  const swings = useMemo(() => {
    if (!inspections) return null;
    const grouped = groupIntoSwings({ inspections, ppfs, handovers });
    // Attach the matching real /swings record (if any) so the row click can
    // launch the PastSwingDrawer with `inspection_ids[]` intact. Prefer
    // matching by inspection-id overlap (robust — labels/dates on the
    // API side are sometimes blank on legacy swings) and fall back to
    // date/shift/crew triplet matching.
    const closed = (swingsRaw || []).filter((r) => {
      const st = String(r.status || "").toLowerCase();
      return st === "closed" || st === "finished" || st === "complete";
    });
    // Pre-index each closed swing's inspection_ids for O(1) lookups.
    const closedByInspId = new Map();
    closed.forEach((r) => {
      (r.inspection_ids || []).forEach((iid) => {
        if (!closedByInspId.has(String(iid))) closedByInspId.set(String(iid), r);
      });
    });
    // For each grouped bucket, collect its inspection ids from the
    // inspection stream and vote on which real swing they belong to.
    const inspByBucket = new Map();
    (inspections || []).forEach((it) => {
      const s = String(it.status || "").toLowerCase();
      if (!["approved", "signed", "closed", "complete", "completed", "submitted"].includes(s)) return;
      const date = bucket(it.approved_at || it.updated_at || it.started_at || it.created_at);
      if (!date) return;
      const shift = (it.shift || "day").toLowerCase() === "night" ? "night" : "day";
      const crew = it.assigned_crew_name || it.crew_name || it.crew || "—";
      const key = `${date}|${shift}|${crew}`;
      if (!inspByBucket.has(key)) inspByBucket.set(key, []);
      inspByBucket.get(key).push(String(it.id || it._id));
    });
    return grouped.map((g) => {
      const ids = inspByBucket.get(g.key) || [];
      // Vote — whichever real closed swing contains the most inspections from
      // this bucket wins.
      const votes = new Map();
      ids.forEach((iid) => {
        const rs = closedByInspId.get(iid);
        if (!rs) return;
        votes.set(rs, (votes.get(rs) || 0) + 1);
      });
      let best = null; let bestVotes = 0;
      votes.forEach((v, r) => { if (v > bestVotes) { best = r; bestVotes = v; } });
      // Fallback — pure date/shift/crew match if inspection-id voting yielded nothing.
      if (!best) {
        best = closed.find((r) => (
          (r.start_date || "").startsWith(g.date) &&
          (String(r.swing_label || r.shift || "").toLowerCase().includes(g.shift)) &&
          (!g.crew || String(r.crew_name || "").toLowerCase() === String(g.crew).toLowerCase())
        )) || null;
      }
      return { ...g, realSwing: best };
    });
  }, [inspections, ppfs, handovers, swingsRaw]);

  const filtered = useMemo(() => {
    if (!swings) return null;
    const q = query.trim().toLowerCase();
    if (!q) return swings;
    return swings.filter((s) =>
      (s.crew || "").toLowerCase().includes(q) ||
      (s.finishedBy || "").toLowerCase().includes(q) ||
      s.date.includes(q) ||
      s.shift.includes(q) ||
      String(s.week).includes(q),
    );
  }, [swings, query]);

  // WO Lookup — collect every inspection with a work_order_number matching
  // the query. Supports partial match (case-insensitive) so a supervisor can
  // type "42" and see every WO ending in 42.
  const woMatches = useMemo(() => {
    if (!inspections) return null;
    const q = woQuery.trim().toLowerCase();
    if (!q) return [];
    const matches = inspections.filter((it) => {
      const wo = String(it.work_order_number || it.wo || "").toLowerCase();
      return wo && wo.includes(q);
    });
    // Sort newest first
    return matches.sort((a, b) => {
      const at = new Date(a.updated_at || a.approved_at || a.started_at || a.created_at || 0).getTime();
      const bt = new Date(b.updated_at || b.approved_at || b.started_at || b.created_at || 0).getTime();
      return bt - at;
    });
  }, [inspections, woQuery]);

  // Group WO matches by WO# so identical work orders across shifts appear
  // together. Very useful when a single WO spans multiple crews / days.
  const woGroups = useMemo(() => {
    if (!woMatches) return null;
    const g = new Map();
    woMatches.forEach((it) => {
      const wo = String(it.work_order_number || it.wo || "").trim();
      if (!wo) return;
      if (!g.has(wo)) g.set(wo, []);
      g.get(wo).push(it);
    });
    return Array.from(g.entries()).map(([wo, items]) => ({ wo, items }));
  }, [woMatches]);

  return (
    <div className="space-y-6" data-testid="swing-history-page">
      {/* Hero + tabs */}
      <section className="panel-heavy p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 justify-between">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-black text-muted flex-wrap">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-1 hover:text-white transition"
              data-testid="swing-history-back"
              title="Back to Swing"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Swing
            </button>
            <span className="text-muted/60">/</span>
            <History className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} />
            Archive · {user?.site_code || "All sites"}
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight leading-[1] mt-2">
            SWING ARCHIVE
          </h1>
          <p className="text-muted mt-2 text-sm">
            Search every finished swing on this site, or hunt down inspections by their work order number.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button className="btn btn-ghost btn-sm" onClick={reload} disabled={busy} data-testid="swing-history-refresh">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
        </div>
      </section>

      {/* Tab strip */}
      <div className="flex items-center gap-1 panel-flat p-1 w-fit" data-testid="archive-tabs">
        <TabButton
          active={tab === "timeline"}
          onClick={() => setTab("timeline")}
          icon={<History className="w-4 h-4" />}
          label="Swing Timeline"
          testid="tab-timeline"
        />
        <TabButton
          active={tab === "wo"}
          onClick={() => setTab("wo")}
          icon={<Hash className="w-4 h-4" />}
          label="Work Order Lookup"
          testid="tab-wo"
        />
      </div>

      {err && (
        <div className="pill pill-red !text-sm !py-2 !px-3 justify-start">
          <AlertTriangle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      {tab === "timeline" && (
        <TimelineTab
          filtered={filtered}
          query={query}
          setQuery={setQuery}
          navigate={navigate}
          onOpenSwing={setOpenSwing}
        />
      )}

      {tab === "wo" && (
        <WoLookupTab
          inspections={inspections}
          woQuery={woQuery}
          setWoQuery={(v) => {
            setWoQuery(v);
            // keep query param in sync for deep-linking
            setParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set("tab", "wo");
              if (v) next.set("wo", v); else next.delete("wo");
              return next;
            });
          }}
          woGroups={woGroups}
          navigate={navigate}
          totalInspections={inspections?.length || 0}
        />
      )}

      {openSwing && (
        <PastSwingDrawer
          swing={openSwing}
          inspections={inspections || []}
          onClose={() => setOpenSwing(null)}
        />
      )}
    </div>
  );
}

/* ----------------- Timeline tab (existing swing history) ----------------- */

function TimelineTab({ filtered, query, setQuery, navigate, onOpenSwing }) {
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 left-3 text-muted" />
          <input
            className="input !pl-9 !w-full"
            placeholder="Search crew, week, date, shift…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="swing-history-search"
          />
        </div>
      </div>

      {filtered === null ? (
        <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading swing history…
        </div>
      ) : filtered.length === 0 ? (
        <div className="panel-flat p-10 text-center" data-testid="swing-history-empty">
          <History className="w-10 h-10 mx-auto mb-3 text-muted" />
          <div className="font-display font-black text-2xl">No finished swings yet</div>
          <div className="text-muted text-sm mt-1">
            The first time a crew taps <span className="text-white font-bold">Finish Swing</span> it&apos;ll appear here.
          </div>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((s) => (
            <SwingRow
              key={s.key}
              swing={s}
              onOpen={() => {
                // Prefer the drill-in drawer (view-only, mirrors active
                // Swing home) using the real /swings record when we found
                // one via inspection-id voting. If no real closed swing
                // matches (legacy off-swing signing, or the swing record
                // is still active while inspections signed today), we
                // synthesise a swing shape from the bucket so the drawer
                // can still render the retro "past record" list. Josh
                // (Jul 12): every archive row must click into a view-only
                // 90 % detail.
                const synthetic = s.realSwing || {
                  id: `synthetic-${s.key}`,
                  crew_name: s.crew,
                  start_date: s.date,
                  swing_label: `Swing · Week ${s.week} · ${s.date} · ${s.shift === "night" ? "Night" : "Day"}`,
                  shift: s.shift,
                  status: "closed",
                  closed_at: s.finishedAt,
                  finished_by_name: s.finishedBy,
                  supervisor_name: s.finishedBy,
                  inspection_ids: s.inspection_ids || [],
                };
                onOpenSwing(synthetic);
              }}
            />
          ))}
        </div>
      )}
    </>
  );
}

/* ----------------- Work Order Lookup tab ----------------- */

function WoLookupTab({ inspections, woQuery, setWoQuery, woGroups, navigate, totalInspections }) {
  return (
    <>
      <div className="panel-flat p-5" data-testid="wo-lookup-block">
        <label className="block text-[11px] uppercase tracking-widest text-muted font-black mb-2">
          Work Order #
        </label>
        <div className="relative">
          <Hash className="w-4 h-4 absolute top-1/2 -translate-y-1/2 left-3 text-muted" />
          <input
            autoFocus
            className="input !pl-9 !text-lg font-mono-tight !py-3"
            placeholder="e.g. WO-12345  (partial match works)"
            value={woQuery}
            onChange={(e) => setWoQuery(e.target.value)}
            data-testid="wo-lookup-input"
          />
        </div>
        <div className="text-[11px] text-muted mt-2">
          Searching across {totalInspections.toLocaleString()} inspection{totalInspections === 1 ? "" : "s"} on this site.
        </div>
      </div>

      {inspections === null ? (
        <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading inspections…
        </div>
      ) : !woQuery.trim() ? (
        <div className="panel-flat p-10 text-center" data-testid="wo-lookup-empty">
          <Hash className="w-10 h-10 mx-auto mb-3 text-muted" />
          <div className="font-display font-black text-2xl">Type a work order # to find every job it&apos;s attached to</div>
          <div className="text-muted text-sm mt-1">
            Every inspection created on Swing gets an optional WO# — this search runs across all shifts, crews and machines.
          </div>
        </div>
      ) : (woGroups && woGroups.length === 0) ? (
        <div className="panel-flat p-10 text-center" data-testid="wo-lookup-no-results">
          <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-muted" />
          <div className="font-display font-black text-2xl">No inspections matched &ldquo;{woQuery}&rdquo;</div>
          <div className="text-muted text-sm mt-1">
            WO#s are set when a new swing is created, or from the inspection detail chip. Double-check the number and try again.
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {woGroups.map((g) => (
            <WoGroupCard key={g.wo} group={g} onOpen={(iid) => navigate(`/inspections?open=${iid}`)} />
          ))}
        </div>
      )}
    </>
  );
}

function WoGroupCard({ group, onOpen }) {
  const worst = worstStatus(group.items);
  return (
    <div className="panel-heavy p-5" data-testid={`wo-group-${group.wo}`}>
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div
          className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center"
          style={{ background: "rgba(245,130,32,0.14)", border: "1px solid rgba(245,130,32,0.35)", color: "var(--primary)" }}
        >
          <Hash className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-2xl tracking-tight break-all">{group.wo}</div>
          <div className="text-xs text-muted mt-0.5">
            {group.items.length} inspection{group.items.length === 1 ? "" : "s"} tagged with this WO
          </div>
        </div>
        <StatusPill status={worst} />
      </div>
      <div className="grid gap-2">
        {group.items.map((it) => (
          <button
            key={it.id || it._id}
            onClick={() => onOpen(it.id || it._id)}
            className="panel-flat p-3 text-left flex items-center gap-3 transition hover:brightness-110"
            data-testid={`wo-insp-${it.id || it._id}`}
          >
            <div className="w-9 h-9 rounded-lg shrink-0 flex items-center justify-center" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid var(--border)" }}>
              <FileText className="w-4 h-4 text-muted" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display font-black text-sm text-white">{it.asset_id || it.asset_name || "—"}</span>
                <span className="text-[11px] text-muted truncate">{it.template_name || it.title || ""}</span>
              </div>
              <div className="flex items-center gap-3 text-[10px] text-muted mt-0.5 uppercase tracking-widest">
                <span className="inline-flex items-center gap-1">
                  <CalendarIcon className="w-3 h-3" /> {fmtDateShort(it.approved_at || it.updated_at || it.started_at || it.created_at)}
                </span>
                {it.assigned_crew_name && (
                  <span className="inline-flex items-center gap-1">
                    <Users className="w-3 h-3" /> {it.assigned_crew_name}
                  </span>
                )}
                {it.shift && (
                  <span className="inline-flex items-center gap-1">
                    {(it.shift || "").toLowerCase() === "night" ? <Moon className="w-3 h-3 text-blue-300" /> : <Sun className="w-3 h-3 text-orange-300" />}
                    {String(it.shift).toUpperCase()}
                  </span>
                )}
              </div>
            </div>
            <StatusPill status={it.status} />
            <ChevronRight className="w-4 h-4 text-muted shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

/* ----------------- Timeline row (unchanged from previous impl) ----------------- */

function SwingRow({ swing, onOpen }) {
  const Icon = swing.shift === "night" ? Moon : Sun;
  const tone = swing.shift === "night"
    ? { bg: "#0f172a", border: "#3b82f6", color: "#93c5fd", label: "NIGHT" }
    : { bg: "#4a2a06", border: "#f58220", color: "#ffb347", label: "DAY" };
  return (
    <button
      onClick={onOpen}
      className="panel-heavy p-5 text-left transition-transform hover:-translate-y-0.5 hover:shadow-lg"
      data-testid={`swing-row-${swing.key}`}
    >
      <div className="flex items-center gap-4">
        <div
          className="w-12 h-12 shrink-0 rounded-xl flex items-center justify-center"
          style={{ background: tone.bg, border: `1px solid ${tone.border}`, color: tone.color }}
        >
          <Icon className="w-6 h-6" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display font-black text-lg tracking-tight">{fmtDate(swing.date)}</div>
            <span
              className="pill !text-[10px] !py-0.5 !px-2 font-black uppercase tracking-widest"
              style={{ background: tone.bg, border: `1px solid ${tone.border}`, color: tone.color }}
            >
              {tone.label} SWING
            </span>
            <span className="pill !text-[10px] !py-0.5 !px-2 font-mono-tight">Week {swing.week}</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap text-xs text-white/70 mt-1.5">
            <span className="inline-flex items-center gap-1">
              <Users className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} /> {swing.crew || "—"}
            </span>
            {swing.finishedBy && (
              <span className="inline-flex items-center gap-1">
                <ClipboardCheck className="w-3.5 h-3.5 text-green-400" /> Finished by <b className="text-white">{swing.finishedBy}</b>
              </span>
            )}
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4 mr-2">
          <Stat label="Inspections" value={swing.inspectionCount} icon={<ClipboardList className="w-3.5 h-3.5" />} color="#4ade80" />
          <Stat label="Planning Requests" value={swing.ppfCount} icon={<Wrench className="w-3.5 h-3.5" />} color="#93c5fd" />
          <Stat label="Handovers" value={swing.handoverCount} icon={<ArrowLeftRight className="w-3.5 h-3.5" />} color="#ffb347" />
        </div>

        <ChevronRight className="w-5 h-5 text-muted shrink-0" />
      </div>

      <div className="md:hidden flex items-center gap-4 mt-3 pt-3" style={{ borderTop: "1px solid var(--border)" }}>
        <Stat label="Inspections" value={swing.inspectionCount} icon={<ClipboardList className="w-3.5 h-3.5" />} color="#4ade80" />
        <Stat label="Planning Requests" value={swing.ppfCount} icon={<Wrench className="w-3.5 h-3.5" />} color="#93c5fd" />
        <Stat label="Handovers" value={swing.handoverCount} icon={<ArrowLeftRight className="w-3.5 h-3.5" />} color="#ffb347" />
      </div>
    </button>
  );
}

function TabButton({ active, onClick, icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs uppercase tracking-widest font-black transition"
      style={{
        background: active ? "linear-gradient(180deg, rgba(245,130,32,0.22), rgba(245,130,32,0.10))" : "transparent",
        color: active ? "var(--primary)" : "#a1a1aa",
        border: active ? "1px solid rgba(245,130,32,0.35)" : "1px solid transparent",
      }}
      data-testid={testid}
    >
      {icon} {label}
    </button>
  );
}

function StatusPill({ status }) {
  const s = String(status || "").toLowerCase();
  let tone = { bg: "rgba(255,255,255,0.05)", border: "var(--border)", color: "#a1a1aa" };
  if (["approved", "signed", "closed", "complete", "completed"].some((k) => s.includes(k))) {
    tone = { bg: "rgba(74,222,128,0.14)", border: "rgba(74,222,128,0.35)", color: "#4ade80" };
  } else if (["submit", "ready", "pending"].some((k) => s.includes(k))) {
    tone = { bg: "rgba(245,130,32,0.14)", border: "rgba(245,130,32,0.35)", color: "var(--primary)" };
  } else if (["draft", "progress", "open"].some((k) => s.includes(k))) {
    tone = { bg: "rgba(147,197,253,0.14)", border: "rgba(147,197,253,0.35)", color: "#93c5fd" };
  }
  return (
    <span
      className="pill !text-[10px] !py-0.5 !px-2 font-black uppercase tracking-widest shrink-0"
      style={{ background: tone.bg, border: `1px solid ${tone.border}`, color: tone.color }}
    >
      {(status || "unknown").replace(/_/g, " ").toUpperCase()}
    </span>
  );
}

function Stat({ label, value, icon, color }) {
  return (
    <div className="text-right">
      <div className="text-[9px] uppercase tracking-widest text-muted flex items-center gap-1 justify-end" style={{ color: "#888" }}>
        <span style={{ color }}>{icon}</span> {label}
      </div>
      <div className="font-display font-black text-2xl tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}

/* ---------------- helpers ---------------- */

function unwrap(d) {
  return Array.isArray(d) ? d : d?.items || d?.results || d?.data || [];
}

function fmtDate(d) {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString([], { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return d;
  }
}

function fmtDateShort(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString([], { day: "2-digit", month: "short", year: "2-digit" });
  } catch {
    return String(d);
  }
}

function isoWeek(d) {
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  return Math.ceil((((dt - yearStart) / 86400000) + 1) / 7);
}

function bucket(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d)) return null;
  return todayLocalIso(d);
}

// Return the "worst" (most needing-attention) status across a WO group so
// the badge on the WO card indicates whether the whole job is done.
function worstStatus(items) {
  const rank = (s) => {
    const st = String(s || "").toLowerCase();
    if (["draft", "progress", "open"].some((k) => st.includes(k))) return 3;
    if (["submit", "ready", "pending"].some((k) => st.includes(k))) return 2;
    if (["approved", "signed", "closed", "complete", "completed"].some((k) => st.includes(k))) return 1;
    return 0;
  };
  let worst = null; let best = 0;
  items.forEach((it) => {
    const r = rank(it.status);
    if (r > best) { best = r; worst = it.status; }
    if (!worst) worst = it.status;
  });
  return worst || "unknown";
}

function groupIntoSwings({ inspections, ppfs, handovers }) {
  const buckets = new Map();
  const ensure = (date, shift, crew) => {
    const key = `${date}|${shift}|${crew || "—"}`;
    if (!buckets.has(key)) {
      buckets.set(key, {
        key,
        date,
        shift,
        crew,
        week: isoWeek(new Date(date + "T00:00:00")),
        inspectionCount: 0,
        ppfCount: 0,
        handoverCount: 0,
        finishedBy: null,
        finishedAt: null,
        inspection_ids: [], // collected so PastSwingDrawer can render tiles
      });
    }
    return buckets.get(key);
  };

  (inspections || []).forEach((it) => {
    const s = String(it.status || "").toLowerCase();
    // "Completed-ish" statuses drive whether this inspection registers as
    // finished work in the bucket count / finishedBy attribution. Partial
    // statuses (in-progress, sent-back, not-started, planned) still get
    // their ID added to `inspection_ids` so the PastSwingDrawer can show
    // them as tiles — Josh (Jul 12): "click back into that swing and say
    // that they only done 90 % of it".
    const isCompletedIsh = ["approved", "signed", "closed", "complete", "completed", "submitted"].includes(s);
    const date = bucket(it.approved_at || it.updated_at || it.started_at || it.created_at);
    if (!date) return;
    const shift = (it.shift || "day").toLowerCase() === "night" ? "night" : "day";
    const b = ensure(date, shift, it.assigned_crew_name || it.crew_name || it.crew);
    b.inspection_ids.push(String(it.id || it._id));
    if (isCompletedIsh) {
      b.inspectionCount += 1;
      if (it.approved_at) {
        const at = new Date(it.approved_at);
        if (!b.finishedAt || at > new Date(b.finishedAt)) {
          b.finishedAt = it.approved_at;
          b.finishedBy = it.supervisor_name || it.approved_by_name || it.approved_by || b.finishedBy;
        }
      }
    }
  });

  (ppfs || []).forEach((p) => {
    const date = bucket(p.completed_at || p.approved_at || p.submitted_at || p.updated_at);
    if (!date) return;
    const shift = (p.shift || "day").toLowerCase() === "night" ? "night" : "day";
    const b = ensure(date, shift, p.crew || p.assigned_crew_name);
    b.ppfCount += 1;
  });

  (handovers || []).forEach((h) => {
    const date = bucket(h.date || h.created_at || h.submitted_at);
    if (!date) return;
    const shift = (h.shift || "day").toLowerCase() === "night" ? "night" : "day";
    const b = ensure(date, shift, h.crew || h.crew_name);
    b.handoverCount += 1;
  });

  return Array.from(buckets.values())
    .filter((b) => b.inspectionCount + b.ppfCount + b.handoverCount > 0)
    .sort((a, b) => (a.date === b.date ? (a.shift < b.shift ? 1 : -1) : (a.date < b.date ? 1 : -1)));
}
